import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../api/apiService";
import {
  validateName,
  validatePhoneNumber,
  validateSlug,
  getErrorMessage,
} from "../utils/helpers";
import "../scss/body.scss";

const Body = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("create"); // 'create' or 'verify'
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    gender: "",
    slug: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setError(null);
    setSuccess(null);
    setFormData({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      gender: "",
      slug: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateCreateForm = () => {
    if (!validateName(formData.firstName)) {
      setError("Please enter a valid first name");
      return false;
    }
    if (!validateName(formData.lastName)) {
      setError("Please enter a valid last name");
      return false;
    }
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setError("Please enter a valid 10-digit phone number");
      return false;
    }
    if (!formData.gender) {
      setError("Please select a gender");
      return false;
    }
    return true;
  };

  const validateVerifyForm = () => {
    if (!validateSlug(formData.slug)) {
      setError(
        "Please enter a valid slug (only lowercase letters, numbers, and hyphens)"
      );
      return false;
    }
    return true;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateCreateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.registerUser({
        phoneNumber: formData.phoneNumber,
        name: `${formData.firstName} ${formData.lastName}`,
        gender: formData.gender,
      });

      if (response.data.success) {
        setSuccess("Family tree created! Redirecting...");
        // Redirect to family tree with slug
        setTimeout(() => {
          navigate(`/family/${response.data.data.user.slug}`);
        }, 1000);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateVerifyForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.verifySlug(formData.slug);

      // Check if the API call was successful
      if (response.data?.success || response.data?.data?.familyTree) {
        setSuccess("Family tree retrieved successfully! Redirecting...");
        // Redirect to family tree
        setTimeout(() => {
          navigate(`/family/${formData.slug}`);
        }, 1000);
      } else {
        setError(
          response.data?.message || "Invalid slug. Please check and try again."
        );
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="body">
      <div className="form-container">
        <h1 className="form-title">Family Tree Explorer</h1>

        <div className="input-mode-toggle">
          <button
            className={`${mode === "create" ? "active" : ""}`}
            onClick={() => handleModeChange("create")}
          >
            Create New Tree
          </button>
          <button
            className={`${mode === "verify" ? "active" : ""}`}
            onClick={() => handleModeChange("verify")}
          >
            Enter Family Slug
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form
          onSubmit={mode === "create" ? handleCreateSubmit : handleVerifySubmit}
        >
          {mode === "create" ? (
            <>
              <div className="form-group-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter your first name"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter your last name"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit phone number"
                  disabled={loading}
                />
                <span className="input-hint">
                  Format: 1234567890 or (123) 456-7890
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="gender">Gender *</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="">-- Select Gender --</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Create Family Tree"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="slug">Family Slug *</label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="Enter family slug (e.g., doe-family)"
                  disabled={loading}
                />
                <span className="input-hint">
                  Example: john-doe or doe-family
                </span>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Access Family Tree"}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Body;
